/**
 * API Client for Users Admin App
 *
 * Provides methods to interact with the Accounting Hub REST API endpoints
 * for the users admin interface.
 */

import { apiRequest, handleApiError } from '@tpct/admin-ui';
import type { User, UserCompany, UsersListResponse, UserFormData } from '@/users/src/types/user';

/**
 * Fetch users list with optional filters
 */
export const fetchUsers = async (
  filters?: {
    page?: number;
    per_page?: number;
    orderby?: string;
    order?: string;
    search?: string;
    role?: string;
  },
  bootstrapKey: string = 'AccountingHubUsersAdmin'
): Promise<UsersListResponse> => {
  const params = new URLSearchParams();
  
  if (filters) {
    if (filters.page) {
      params.append('page', filters.page.toString());
    }
    if (filters.per_page) {
      params.append('per_page', filters.per_page.toString());
    }
    if (filters.orderby) {
      params.append('orderby', filters.orderby);
    }
    if (filters.order) {
      params.append('order', filters.order);
    }
    if (filters.search) {
      params.append('search', filters.search);
    }
    if (filters.role) {
      params.append('role', filters.role);
    }
  }

  const queryString = params.toString();
  const endpoint = `users${queryString ? `?${queryString}` : ''}`;
  
  const response = await apiRequest<UsersListResponse>(endpoint, {}, bootstrapKey);
  
  return response;
};

/**
 * Fetch single user
 */
export const fetchUser = async (
  userId: number,
  bootstrapKey: string = 'AccountingHubUsersAdmin'
): Promise<User> => {
  const endpoint = `users/${userId}`;
  const response = await apiRequest<User>(endpoint, {}, bootstrapKey);
  return response;
};

/**
 * Create a new user
 */
export const createUser = async (
  data: UserFormData,
  bootstrapKey: string = 'AccountingHubUsersAdmin'
): Promise<User> => {
  const endpoint = 'users';
  const response = await apiRequest<User>(
    endpoint,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
    bootstrapKey
  );
  return response;
};

/**
 * Update an existing user
 */
export const updateUser = async (
  userId: number,
  data: Partial<UserFormData>,
  bootstrapKey: string = 'AccountingHubUsersAdmin'
): Promise<User> => {
  const endpoint = `users/${userId}`;
  const response = await apiRequest<User>(
    endpoint,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    },
    bootstrapKey
  );
  return response;
};

/**
 * Delete a user
 */
export const deleteUser = async (
  userId: number,
  bootstrapKey: string = 'AccountingHubUsersAdmin'
): Promise<void> => {
  const endpoint = `users/${userId}`;
  await apiRequest<void>(
    endpoint,
    {
      method: 'DELETE',
    },
    bootstrapKey
  );
};

export const revokeUserCompanyAccess = async (
  userId: number,
  companyId: number,
  bootstrapKey: string = 'AccountingHubUsersAdmin',
): Promise<{ success: true; data: { all_companies: boolean; company_ids: number[] } }> => {
  const endpoint = `user-management/users/${userId}/company-scope/${companyId}`;
  const response = await apiRequest<{ success: true; data: { all_companies: boolean; company_ids: number[] } }>(
    endpoint,
    {
      method: 'DELETE',
    },
    bootstrapKey,
  );
  return response;
};

export type AssignableAttributionUser = { id: number; name: string };

export type AssignableAttributionTargets = {
  assignment_kind: 'companies' | 'via_users';
  via_role_slug: string | null;
  companies: UserCompany[];
  users: AssignableAttributionUser[];
};

/** @deprecated use fetchAssignableAttributionTargets when assignment_kind needed */
export const fetchAssignableCompanies = async (
  userId: number,
  bootstrapKey: string = 'AccountingHubUsersAdmin',
): Promise<UserCompany[]> => {
  const targets = await fetchAssignableAttributionTargets(userId, bootstrapKey);
  return targets.companies;
};

export const fetchAssignableAttributionTargets = async (
  userId: number,
  bootstrapKey: string = 'AccountingHubUsersAdmin',
): Promise<AssignableAttributionTargets> => {
  const endpoint = `user-management/users/${userId}/assignable-companies`;
  const response = await apiRequest<AssignableAttributionTargets>(endpoint, {}, bootstrapKey);
  return response;
};

export const saveUserCompanyScope = async (
  userId: number,
  companyIds: number[],
  bootstrapKey: string = 'AccountingHubUsersAdmin',
): Promise<{ all_companies: boolean; company_ids: number[]; via_collaborator_user_ids?: number[] }> => {
  const endpoint = `user-management/users/${userId}/company-scope`;
  const response = await apiRequest<{ all_companies: boolean; company_ids: number[]; via_collaborator_user_ids?: number[] }>(
    endpoint,
    {
      method: 'PUT',
      body: JSON.stringify({
        all_companies: false,
        company_ids: companyIds,
      }),
    },
    bootstrapKey,
  );
  return response;
};

export const saveExpertViaCollaboratorScope = async (
  userId: number,
  viaUserIds: number[],
  bootstrapKey: string = 'AccountingHubUsersAdmin',
): Promise<{ all_companies: boolean; company_ids: number[]; via_collaborator_user_ids?: number[] }> => {
  const endpoint = `user-management/users/${userId}/company-scope`;
  return apiRequest(
    endpoint,
    {
      method: 'PUT',
      body: JSON.stringify({
        via_user_ids: viaUserIds,
      }),
    },
    bootstrapKey,
  );
};

export { handleApiError };

